import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Bell, Users, Calendar, Package, Settings, Plus, X, AlertTriangle, Loader2, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Alert category definitions
const ALERT_CATEGORIES = [
  {
    id: 'lead_alerts',
    name: 'Lead Alerts',
    icon: Users,
    description: 'New leads, lead assignments, sales alerts'
  },
  {
    id: 'consultation_alerts',
    name: 'Consultation Alerts',
    icon: Calendar,
    description: 'New consultations, Zoom link needed, approvals'
  },
  {
    id: 'booking_alerts',
    name: 'Booking Alerts',
    icon: Package,
    description: 'LTT bookings, reschedules, cancellations'
  },
  {
    id: 'system_alerts',
    name: 'System Alerts',
    icon: Settings,
    description: 'Scheduler errors, technical issues'
  }
];

export default function AlertsTab({ sessionToken }) {
  const [settings, setSettings] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [newCustomEmail, setNewCustomEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  // Fetch settings and team members on mount
  useEffect(() => {
    fetchData();
  }, [sessionToken]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch notification settings
      const settingsRes = await fetch(`${BACKEND_URL}/api/v1/admin/notification-settings`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (settingsRes.ok) {
        const settingsData = await settingsRes.json();
        setSettings(settingsData);
      } else {
        toast.error('Failed to load notification settings');
      }

      // Fetch team members
      const teamRes = await fetch(`${BACKEND_URL}/api/v1/admin/team/members`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (teamRes.ok) {
        const teamData = await teamRes.json();
        setTeamMembers(Array.isArray(teamData) ? teamData : []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load alert settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleCategory = (categoryId, enabled) => {
    setSettings(prev => ({
      ...prev,
      [categoryId]: {
        ...prev[categoryId],
        enabled
      }
    }));
    setHasChanges(true);
  };

  const handleToggleRecipient = (categoryId, email, checked) => {
    setSettings(prev => {
      const currentRecipients = prev[categoryId]?.recipients || [];
      const newRecipients = checked
        ? [...currentRecipients, email]
        : currentRecipients.filter(e => e !== email);
      
      return {
        ...prev,
        [categoryId]: {
          ...prev[categoryId],
          recipients: newRecipients
        }
      };
    });
    setHasChanges(true);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  const handleAddCustomEmail = () => {
    const email = newCustomEmail.trim().toLowerCase();
    
    if (!email) {
      setEmailError('Please enter an email address');
      return;
    }
    
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    if (settings.custom_emails?.includes(email)) {
      setEmailError('This email is already added');
      return;
    }
    
    // Check if it's already a team member email
    if (teamMembers.some(m => m.email?.toLowerCase() === email)) {
      setEmailError('This email belongs to a team member');
      return;
    }
    
    setSettings(prev => ({
      ...prev,
      custom_emails: [...(prev.custom_emails || []), email]
    }));
    setNewCustomEmail('');
    setEmailError('');
    setHasChanges(true);
    toast.success('Custom email added');
  };

  const handleRemoveCustomEmail = (email) => {
    // Remove from custom_emails pool
    setSettings(prev => ({
      ...prev,
      custom_emails: (prev.custom_emails || []).filter(e => e !== email),
      // Also remove from all category recipients
      lead_alerts: {
        ...prev.lead_alerts,
        recipients: (prev.lead_alerts?.recipients || []).filter(e => e !== email)
      },
      consultation_alerts: {
        ...prev.consultation_alerts,
        recipients: (prev.consultation_alerts?.recipients || []).filter(e => e !== email)
      },
      booking_alerts: {
        ...prev.booking_alerts,
        recipients: (prev.booking_alerts?.recipients || []).filter(e => e !== email)
      },
      system_alerts: {
        ...prev.system_alerts,
        recipients: (prev.system_alerts?.recipients || []).filter(e => e !== email)
      }
    }));
    setHasChanges(true);
    toast.success('Custom email removed');
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/notification-settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setHasChanges(false);
        if (result.warnings && result.warnings.length > 0) {
          toast.warning(`Settings saved with warnings: ${result.warnings.length} categories have no recipients`);
        } else {
          toast.success('Alert settings saved successfully');
        }
      } else {
        toast.error(result.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save alert settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Count categories with no recipients
  const getEmptyCategoryCount = () => {
    if (!settings) return 0;
    return ALERT_CATEGORIES.filter(cat => {
      const catSettings = settings[cat.id];
      return catSettings?.enabled && (!catSettings?.recipients || catSettings.recipients.length === 0);
    }).length;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="alerts-loading">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
        <span className="ml-3 text-gray-600">Loading alert settings...</span>
      </div>
    );
  }

  if (!settings) {
    return (
      <Alert variant="destructive" data-testid="alerts-error">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Failed to load notification settings. Please refresh the page.
        </AlertDescription>
      </Alert>
    );
  }

  const emptyCategoryCount = getEmptyCategoryCount();

  return (
    <div className="space-y-6" data-testid="alerts-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-navy flex items-center gap-2">
            <Bell className="w-6 h-6" />
            Alert Settings
          </h2>
          <p className="text-gray-600 mt-1">
            Configure which email addresses receive different types of alerts
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="bg-gradient-to-r from-[#E8D89E] via-[#D4AF37] to-[#B99610] text-black font-semibold"
          data-testid="save-alerts-btn"
        >
          {isSaving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
          ) : (
            <><Save className="w-4 h-4 mr-2" /> Save Changes</>
          )}
        </Button>
      </div>

      {/* Warning Banner */}
      {emptyCategoryCount > 0 && (
        <Alert className="bg-amber-50 border-amber-200" data-testid="no-recipients-warning">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Setup Required:</strong> {emptyCategoryCount} alert {emptyCategoryCount === 1 ? 'category has' : 'categories have'} no recipients configured. 
            Alerts will not be sent until recipients are added.
          </AlertDescription>
        </Alert>
      )}

      {/* Alert Category Cards */}
      {ALERT_CATEGORIES.map((category) => {
        const Icon = category.icon;
        const catSettings = settings[category.id] || { enabled: true, recipients: [] };
        const recipients = catSettings.recipients || [];
        const hasNoRecipients = catSettings.enabled && recipients.length === 0;

        return (
          <Card key={category.id} className="bg-ivory" data-testid={`alert-category-${category.id}`}>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-navy/10 rounded-lg">
                    <Icon className="w-5 h-5 text-navy" />
                  </div>
                  <div>
                    <CardTitle className="text-navy text-lg">{category.name}</CardTitle>
                    <p className="text-sm text-gray-600 mt-0.5">{category.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor={`toggle-${category.id}`} className="text-sm text-gray-600">
                    {catSettings.enabled ? 'Enabled' : 'Disabled'}
                  </Label>
                  <Switch
                    id={`toggle-${category.id}`}
                    checked={catSettings.enabled}
                    onCheckedChange={(checked) => handleToggleCategory(category.id, checked)}
                    data-testid={`toggle-${category.id}`}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {catSettings.enabled ? (
                <div className="space-y-4">
                  {/* Team Members Section */}
                  <div>
                    <h4 className="text-sm font-medium text-navy mb-3">Team Members</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {teamMembers.map((member) => (
                        <div key={member.email} className="flex items-center space-x-3 py-2 px-3 bg-white rounded-lg">
                          <Checkbox
                            id={`${category.id}-${member.email}`}
                            checked={recipients.includes(member.email)}
                            onCheckedChange={(checked) => handleToggleRecipient(category.id, member.email, checked)}
                            data-testid={`checkbox-${category.id}-${member.email}`}
                          />
                          <Label 
                            htmlFor={`${category.id}-${member.email}`} 
                            className="flex items-center gap-2 cursor-pointer flex-1"
                          >
                            <span className="text-navy font-medium">
                              {member.name || 'No name set'}
                            </span>
                            <span className="text-gray-500 text-sm">({member.email})</span>
                            <Badge variant="outline" className="text-xs ml-auto">
                              {member.role}
                            </Badge>
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Custom Emails Section */}
                  {settings.custom_emails && settings.custom_emails.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-navy mb-3">Custom Emails</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {(settings.custom_emails || []).map((email) => (
                          <div key={email} className="flex items-center space-x-3 py-2 px-3 bg-white rounded-lg">
                            <Checkbox
                              id={`${category.id}-custom-${email}`}
                              checked={recipients.includes(email)}
                              onCheckedChange={(checked) => handleToggleRecipient(category.id, email, checked)}
                              data-testid={`checkbox-${category.id}-custom-${email}`}
                            />
                            <Label 
                              htmlFor={`${category.id}-custom-${email}`} 
                              className="flex items-center gap-2 cursor-pointer flex-1"
                            >
                              <span className="text-navy">{email}</span>
                              <Badge variant="outline" className="text-xs ml-auto">custom</Badge>
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Warning for no recipients */}
                  {hasNoRecipients && (
                    <Alert className="bg-amber-50 border-amber-200">
                      <AlertTriangle className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-700 text-sm">
                        No recipients selected - {category.name.toLowerCase()} will not be sent
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm italic">
                  This alert category is disabled. Enable it to configure recipients.
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Custom Email Management */}
      <Card className="bg-ivory" data-testid="custom-email-manager">
        <CardHeader>
          <CardTitle className="text-navy text-lg flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Manage Custom Emails
          </CardTitle>
          <p className="text-sm text-gray-600">
            Add email addresses that are not team members
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Add new email */}
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  type="email"
                  placeholder="Enter email address"
                  value={newCustomEmail}
                  onChange={(e) => {
                    setNewCustomEmail(e.target.value);
                    setEmailError('');
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCustomEmail()}
                  className={emailError ? 'border-red-500' : ''}
                  data-testid="new-custom-email-input"
                />
                {emailError && (
                  <p className="text-red-500 text-sm mt-1">{emailError}</p>
                )}
              </div>
              <Button
                onClick={handleAddCustomEmail}
                variant="outline"
                className="border-navy text-navy hover:bg-navy hover:text-white"
                data-testid="add-custom-email-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add to Pool
              </Button>
            </div>

            {/* Current custom emails */}
            {settings.custom_emails && settings.custom_emails.length > 0 ? (
              <div>
                <h4 className="text-sm font-medium text-navy mb-2">Current Pool ({settings.custom_emails.length})</h4>
                <div className="space-y-2">
                  {(settings.custom_emails || []).map((email) => (
                    <div key={email} className="flex items-center justify-between py-2 px-3 bg-white rounded-lg">
                      <span className="text-navy">{email}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveCustomEmail(email)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-custom-${email}`}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm italic">
                No custom emails added yet. Add emails above to use them in alert categories.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Last Updated */}
      {settings.updated_at && (
        <p className="text-sm text-gray-500 text-right">
          Last updated: {new Date(settings.updated_at).toLocaleString()}
          {settings.updated_by && ` by ${settings.updated_by}`}
        </p>
      )}
    </div>
  );
}
